import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionOneTitleTextsApi from "hooks/api/private/blocks/useSectionOneTitleTextsApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionOneTitleTextsBody,
    SectionOneTitleTextsDetails,
} from "types/api/blocks/sectionOneTitleTexts";
import { LanguageEnum } from "types/api/commons";

export type SectionOneTitleTextsForm = {
    id: string | undefined;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            text: string;
            button_label: string;
            button_url: string;
        };
    };
};

type SectionOneTitleTextsType = {
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneTitleTextsForm>;
};

function useSectionOneTitleTexts(): SectionOneTitleTextsType {
    const { saveSectionOneTitleTexts, getOneSectionOneTitleTexts, deleteSectionOneTitleTexts } =
        useSectionOneTitleTextsApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title: {
                                type: "simple",
                                validators: [],
                            },
                            subtitle: {
                                type: "simple",
                                validators: [],
                            },
                            text: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            button_label: {
                                type: "simple",
                                validators: [],
                            },
                            button_url: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: SectionOneTitleTextsForm = React.useMemo(() => {
        return {
            id: undefined,
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneTitleTextsForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionOneTitleTexts, setsectionOneTitleTexts] = React.useState<
        SectionOneTitleTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOneTitleTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setsectionOneTitleTexts).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setsectionOneTitleTexts, getOneSectionOneTitleTexts, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneTitleTexts) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneTitleTexts.id;
                newForm.text = sectionOneTitleTexts.text;

                return newForm;
            });
        }
    }, [setForm, sectionOneTitleTexts]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const body: SectionOneTitleTextsBody = {
                    id: blockGroupCompositionId,
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: form.text.it.subtitle,
                            text: form.text.it.text,
                            button_label: form.text.it.button_label,
                            button_url: form.text.it.button_url,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: form.text.en.subtitle,
                            text: form.text.en.text,
                            button_label: form.text.en.button_label,
                            button_url: form.text.en.button_url,
                        },
                    },
                };

                const [apiCall] = saveSectionOneTitleTexts(body);

                const promises: Array<Promise<unknown>> = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOneTitleTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        graphicalOrder,
        blockGroupId,
        blockId,
        isValid,
        form,
    ]);

    return React.useMemo(
        (): SectionOneTitleTextsType => ({
            buttonDelete: deleteSectionOneTitleTexts,
            formHook,
        }),
        [deleteSectionOneTitleTexts, formHook]
    );
}

export default useSectionOneTitleTexts;
